import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API_TAGS } from '@/shared/api';
import { dryRunPipeline, fetchPipeline, fetchPipelines, fetchRun, fetchRuns, restartRun } from './api';
import { DryRunRequest, PipelinesListParams, RestartRunRequest, RunDetailResponse, RunsListParams } from './types';

export const jobsKeys = {
  all: [API_TAGS.JOBS] as const,
  pipelines: () => [...jobsKeys.all, 'pipelines'] as const,
  pipelinesList: (params?: PipelinesListParams) => [...jobsKeys.pipelines(), 'list', params] as const,
  pipelineDetail: (nameOrId: string) => [...jobsKeys.pipelines(), 'detail', nameOrId] as const,
  runs: () => [...jobsKeys.all, 'runs'] as const,
  runsList: (params?: RunsListParams) => [...jobsKeys.runs(), 'list', params] as const,
  runDetail: (runId: string) => [...jobsKeys.runs(), 'detail', runId] as const,
};

export const usePipelines = (params?: PipelinesListParams) => {
  return useQuery({
    queryKey: jobsKeys.pipelinesList(params),
    queryFn: () => fetchPipelines(params),
    placeholderData: keepPreviousData,
  });
};

export const usePipeline = (nameOrId: string, options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: jobsKeys.pipelineDetail(nameOrId),
    queryFn: () => fetchPipeline(nameOrId),
    enabled: options?.enabled ?? true,
  });
};

export const useDryRunPipeline = () => {
  return useMutation({
    mutationFn: ({ nameOrId, payload }: { nameOrId: string; payload?: DryRunRequest }) =>
      dryRunPipeline(nameOrId, payload),
  });
};

export const useRuns = (params?: RunsListParams, options?: { refetchInterval?: number | false }) => {
  return useQuery({
    queryKey: jobsKeys.runsList(params),
    queryFn: () => fetchRuns(params),
    placeholderData: keepPreviousData,
    refetchInterval: options?.refetchInterval,
  });
};

/**
 * Интервал опроса запуска: числом — постоянный, функцией — по последнему полученному состоянию
 * запуска (`undefined`, пока ответа ещё нет), чтобы опрос прекращался вместе с запуском.
 */
type RunRefetchInterval = number | false | ((run: RunDetailResponse | undefined) => number | false);

export const useRun = (
  runId: string,
  options?: { enabled?: boolean; retry?: number | boolean; refetchInterval?: RunRefetchInterval },
) => {
  const refetchInterval = options?.refetchInterval;
  return useQuery({
    queryKey: jobsKeys.runDetail(runId),
    queryFn: () => fetchRun(runId),
    enabled: options?.enabled ?? true,
    retry: options?.retry ?? 1,
    refetchInterval:
      typeof refetchInterval === 'function' ? (query) => refetchInterval(query.state.data) : refetchInterval,
  });
};

export const useRestartRun = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ runId, payload }: { runId: string; payload?: RestartRunRequest }) => restartRun(runId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobsKeys.runs() });
    },
  });
};
